import React, { ReactNode } from 'react';
import { DictionaryService } from 'service/dictionary/dictionaryService';
import type TRecipeStep from 'types/RecipeStep';
import { DictionaryItem } from 'types/Recipes';
import parseRecipeStep from 'components/RecipeStep/helpers/parseRecipeStep';
import { Title } from 'components/Typography';
import { WavyCircleIcon } from '@icons/WavyCircle';
import { useUserAgent } from 'hooks/useUserAgent';

export const RecipeStepPrint = ({
  steps,
  dictionary,
}: {
  steps: any;
  dictionary: DictionaryItem[];
}): ReactNode => {
  let itemsToBeRemoved: number[] = [];
  let stepNumber: number = 0;

  const { isSafari } = useUserAgent();

  const SubStep = ({
    innerRecipeStepText,
    dictionaryKey,
  }: {
    innerRecipeStepText?: string;
    dictionaryKey?: string;
  }) => {
    if (!innerRecipeStepText) {
      return null;
    }

    return (
      <p className="font-primary tracking-wide font-bold text-sm">
        {!!dictionaryKey && <>{DictionaryService.getCopy(dictionaryKey, dictionary)}: </>}
        {innerRecipeStepText}
      </p>
    );
  };

  const data = steps.map((step: any, index: number) => {
    // Parse recipe step into concise object
    const recipeStep: TRecipeStep = parseRecipeStep(step);
    const innerSteps: TRecipeStep[] = [];
    if (!itemsToBeRemoved.includes(index)) {
      for (let i = index + 1; i < steps.length; i += 1) {
        const tempRecipeStep: TRecipeStep = parseRecipeStep(steps[i]);
        if (!tempRecipeStep.isStep) {
          innerSteps.push(tempRecipeStep);
          itemsToBeRemoved.push(i);
        } else {
          break;
        }
      }
    }

    const title = recipeStep.title || '';
    const isStep = recipeStep?.isStep || false;

    const generateSubStep = (innerRecipeStep: TRecipeStep) => (
      <>
        {!!innerRecipeStep.description && (
          <p
            className="text-sm step-links"
            dangerouslySetInnerHTML={{ __html: innerRecipeStep.description }}
          />
        )}
        <SubStep
          dictionaryKey={'rack_position'}
          innerRecipeStepText={innerRecipeStep.rackPosition}
        />
        <SubStep innerRecipeStepText={innerRecipeStep.cookingSummaryText} />
        <SubStep
          dictionaryKey={'top_and_bottom_heat_oven_temperature'}
          innerRecipeStepText={innerRecipeStep.conventionalOvenTemp}
        />
        <SubStep
          dictionaryKey={'fan_assisted_oven_temperature'}
          innerRecipeStepText={innerRecipeStep.fanOvenTemp}
        />
      </>
    );

    const body = (
      <div className="m-0">
        {!!recipeStep?.ingredients?.length && (
          <>
            <div className="w-full md:mb-5 mt-0.5">
              {recipeStep?.ingredients &&
                recipeStep?.ingredients.map((ingredient: any, idx: number) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div className="mb-1" key={`ingredient-${idx}`}>
                    <span className="text-sm font-bold pr-1.5 leading-6 font-primary">
                      {ingredient.AmountAndUnitSummaryText}
                    </span>
                    <span className="text-sm font-primary">{ingredient.ArticleSummaryText}</span>
                  </div>
                ))}
            </div>
          </>
        )}
        <div className="w-full">
          {generateSubStep(recipeStep)}

          {innerSteps.length > 0 &&
            innerSteps.map((item: TRecipeStep, idx: number) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={`recipe-step-${idx}`}>
                {item.title && (
                  <Title tag="div" type="sm" className="mb-8 print:text-sm">
                    {item.title}
                  </Title>
                )}
                {generateSubStep(item)}
              </React.Fragment>
            ))}
        </div>
      </div>
    );
    return {
      title,
      body,
      isStep,
    };
  });

  itemsToBeRemoved = itemsToBeRemoved.sort((a, b) => b - a);

  itemsToBeRemoved.forEach((item) => {
    data.splice(item, 1);
  });

  const PanelContent = (item: any, index: number) => {
    // do not render title as step
    if (index === 0 && !item?.isStep && item.title) {
      return null;
    }
    if (item?.isStep) {
      stepNumber++;
    }
    return (
      <div className="preparation-blocks-steps break-inside-auto" key={`${index}-recipe-step`}>
        <h2 className={'print:break-after-avoid text-sm mt-3 mb-0 flex'}>
          {item?.isStep && (
            <div className="relative mr-1 w-[21px] h-[21px]">
              <WavyCircleIcon />
              <div className="absolute left-0 flex items-center justify-center -inset-0">
                {stepNumber}
              </div>
            </div>
          )}

          {item.title}
        </h2>
        {item.body}
      </div>
    );
  };

  const combinedItems = [...data];

  const titleItem = !combinedItems[0].isStep && combinedItems[0].title ? combinedItems[0] : null;

  return (
    <>
      {titleItem && (
        <h2 className={'print:break-after-avoid text-[18px] uppercase mt-6'}>{titleItem.title}</h2>
      )}

      <div className={isSafari ? '' : 'columns-2'}>{combinedItems.map(PanelContent)}</div>
    </>
  );
};
